import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import EditOutlined from '@ant-design/icons/EditOutlined'
import FileOutlined from '@ant-design/icons/FileOutlined'
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined'
import MoreOutlined from '@ant-design/icons/MoreOutlined'
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined'
import { Button, Dropdown, Menu, message, Modal } from 'antd'
import { inject, observer } from 'mobx-react'
import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { blue } from '../../../color'
import AccessControlAction from '../../../components/AccessControlAction'
import { StatusTag } from '../../../components/Common/StatusTag'
import PageTitle from '../../../components/PageTitle'
import { systemName } from '../../../config'
import {
  ACL_ACTION_TYPE,
  ASSIGNEE_TYPE,
  ELECTRONIC_DOCUMENT_STATUS,
  TYPE_STATUS,
} from '../../../constants'
import useOpenItem from '../../../hooks/useOpenItem'
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout'
import { HeadingButtonWrapper } from '../../../layouts/InternalDocumentDetailLayout/InternalDocumentDetailLayoutStyled'
import PopupUpdateSignDoc from '../InternalDocumentSignDocPage/PopupUpdateSignDoc/PopupUpdateSignDoc'
import CanvasDoc from './CanvasDoc'
import InternalDocumentSignDocUpdateAnnex from './InternalDocumentSignDocUpdateAnnex'
import PopupAddPersonInternalDocDetail from './PopupAddPersonInternalDocDetail'
import TableFileItem from './TableFileItem'

const { confirm } = Modal

const InternalDocumentSignDocDetailPage = props => {
  const {
    loadingAnimationStore,
    history,
    fileStore,
    match,
    electronicDocumentStore,
    signedDocumentStore,
    notificationStore,
    listHistoryLogStore,
    authenticationStore,
    location,
    onStepChange, // Thêm prop để nhận currentStep
  } = props
  const { documentId } = match.params
  const { historyLists } = listHistoryLogStore
  const { selectedElectronicDocument } = electronicDocumentStore
  const { currentUser } = authenticationStore
  const {
    isOpen: isVisibleAddPersonWork,
    handleOpen: handleOpenAddPersonWork,
    handleClose: handleClosePopupAddPersonWork,
  } = useOpenItem()

  const {
    isOpen: isVisibleEdit,
    handleOpen: handleOpenEdit,
    handleClose: handleCloseEdit,
  } = useOpenItem()
  const [isVTXL, setVTXL] = useState(null)
  const [isFileModalVisible, setIsFileModalVisible] = useState(false)
  const [isFileModalEnnexVisible, setIsFileModalEnnexVisible] = useState(false)
  const [currentStep, setCurrentStep] = useState(0) // State để lưu currentStep

  const steps = [
    'PENDING',
    'TP_APPROVAL',
    'TP_APPROVAL', // Lưu ý: Có thể là lỗi, nên kiểm tra lại
    'LEAD_APPROVAL',
    'REGIS_NUMBER',
    'READY',
    'RELEASE',
  ]

  const showFileModal = useCallback(() => {
    setIsFileModalVisible(true)
  }, [])

  const showFileModalEnnex = useCallback(() => {
    setIsFileModalEnnexVisible(true)
  }, [])

  const handleFileModalClose = useCallback(e => {
    e.stopPropagation()
    setIsFileModalVisible(false)
  }, [])

  const handleFileModalEnnexClose = useCallback(() => {
    getElectronicDocumentById()
    setIsFileModalEnnexVisible(false)
  }, [])

  const getBackUrl = useMemo(() => {
    if (location.state && location.state.from) {
      return location.state.from
    }
    if (
      document.referrer &&
      document.referrer.includes('/internal-document/sign-document')
    ) {
      return document.referrer.split(window.location.origin)[1]
    }
    return '/internal-document/sign-document'
  }, [location.state])

  const getElectronicDocumentById = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)
      const { data } = await electronicDocumentStore.getElectronicDocumentById(
        documentId
      )
      await fileStore.downloadAttachment(
        data.action_history.find(el => el.action === data.status).file_id
      )
    } catch (err) {
      history.push('/internal-document/sign-document')
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [documentId, electronicDocumentStore, fileStore, history])

  useEffect(() => {
    const checkVTXL = authenticationStore.isCheckVTXL
    setVTXL(checkVTXL)
  }, [authenticationStore?.currentUser])

  useEffect(() => {
    getElectronicDocumentById()

    return () => {
      electronicDocumentStore.clearSelectedElectronicDocument()
      electronicDocumentStore.clearCommentElectronicDocumentList()
      signedDocumentStore.clearDataPopupSign()
      signedDocumentStore.clearSignatureList()
      fileStore.clearDocumentAttachment()
      notificationStore.clearNotificationType()
    }
  }, [documentId])

  const handleDeleteElectronicDocument = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)
      await electronicDocumentStore.deleteElectronicDocument(documentId)
      history.push('/internal-document/sign-document')
      message.success('Xóa trình ký thành công!')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [documentId, electronicDocumentStore, history])

  const showConfirm = useCallback(() => {
    confirm({
      title: 'Bạn có muốn xóa trình ký này không?',
      icon: <QuestionCircleOutlined style={{ color: 'red' }} />,
      okText: 'Đồng ý',
      cancelText: 'Không',
      okType: 'danger',
      onOk() {
        handleDeleteElectronicDocument()
      },
    })
  }, [handleDeleteElectronicDocument])

  const canShowDelete = useMemo(() => {
    if (!selectedElectronicDocument || !currentUser?.username) return false
    if (currentStep == null) return false

    const { status, assignees = [] } = selectedElectronicDocument

    const hasPerm = perm =>
      assignees.some(
        a => a.permission === perm && a.assignee_code === currentUser.username
      )

    // Helper: Kiểm tra có assignee cho permission không
    const hasAssignee = perm => assignees.some(a => a.permission === perm)

    // ==================================================================
    // 1. ĐÃ PHÁT HÀNH → CẤM XÓA
    // ==================================================================
    if (status === ELECTRONIC_DOCUMENT_STATUS.RELEASE) {
      return false
    }

    // ==================================================================
    // 2. CÁC TRẠNG THÁI BỊ HỦY/TRẢ LẠI → NGƯỜI Ở currentStep HIỆN TẠI ĐƯỢC XÓA
    // ==================================================================
    // Lưu ý: currentStep đã được tính toán dựa trên status + quyền của user
    // Nên chỉ cần check người ở currentStep hiện tại

    const REJECTION_STATUSES = [
      ELECTRONIC_DOCUMENT_STATUS.VT_REJECTION,
      ELECTRONIC_DOCUMENT_STATUS.LEAD_REJECTION,
      ELECTRONIC_DOCUMENT_STATUS.TP_REVERT,
      ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER_REJECTION,
      ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER_REVERT,
      ELECTRONIC_DOCUMENT_STATUS.READY_REJECTION,
      ELECTRONIC_DOCUMENT_STATUS.RELEASE_REJECTION,
    ]

    if (REJECTION_STATUSES.includes(status)) {
      // Khi bị reject, currentStep đã tự động quay về bước trước
      // Người ở bước đó (currentStep) được xóa
      const STEP_PERMISSIONS = {
        0: ['CREATE'],
        1: ['DEPARTMENT_APPROVER', 'DEPARTMENT_CO_SIGNER'],
        2: ['FORM_APPROVER'],
        3: ['LEADER_APPROVER'],
        4: ['NUMBER_ISSUER'],
        5: ['DIGITAL_SIGNER'],
        6: ['RELEASER'],
      }

      const allowedPerms = STEP_PERMISSIONS[currentStep] || []
      return allowedPerms.some(hasPerm)
    }

    // ==================================================================
    // 3. CRE_APPROVAL (ĐÃ TRÌNH DUYỆT) - TRƯỜNG HỢP ĐỘC LẠP
    // ==================================================================
    // Status này đặc biệt vì có thể rơi vào nhiều step khác nhau
    // Tùy vào workflow có bỏ qua bước nào không
    if (status === ELECTRONIC_DOCUMENT_STATUS.CRE_APPROVAL) {
      // currentStep cho biết người nào đang có lượt xử lý
      const STEP_PERMISSIONS = {
        1: ['DEPARTMENT_APPROVER', 'DEPARTMENT_CO_SIGNER'],
        2: ['FORM_APPROVER'],
        3: ['LEADER_APPROVER'],
        4: ['NUMBER_ISSUER'],
        5: ['DIGITAL_SIGNER'],
        6: ['RELEASER'],
        7: [], // Không ai được xóa nếu đã qua hết workflow
      }

      const allowedPerms = STEP_PERMISSIONS[currentStep] || []
      return allowedPerms.some(hasPerm)
    }

    // ==================================================================
    // 4. TP_APPROVAL (TRƯỞNG PHÒNG ĐÃ DUYỆT) - TRƯỜNG HỢP ĐỘC LẬP
    // ==================================================================
    // Tương tự CRE_APPROVAL, có thể skip các bước tiếp theo
    if (status === ELECTRONIC_DOCUMENT_STATUS.TP_APPROVAL) {
      const STEP_PERMISSIONS = {
        2: ['FORM_APPROVER'],
        3: ['LEADER_APPROVER'],
        4: ['NUMBER_ISSUER'],
        5: ['DIGITAL_SIGNER'],
        6: ['RELEASER'],
        7: [], // Không ai được xóa
      }

      const allowedPerms = STEP_PERMISSIONS[currentStep] || []
      return allowedPerms.some(hasPerm)
    }

    // ==================================================================
    // 5. VT_APPROVAL / VT_SIGNED (VĂN THƯ ĐÃ DUYỆT/KÝ) - TRƯỜNG HỢP ĐỘC LẬP
    // ==================================================================
    if (
      status === ELECTRONIC_DOCUMENT_STATUS.VT_APPROVAL ||
      status === ELECTRONIC_DOCUMENT_STATUS.VT_SIGNED
    ) {
      const STEP_PERMISSIONS = {
        3: ['LEADER_APPROVER'],
        4: ['NUMBER_ISSUER'],
        5: ['DIGITAL_SIGNER'],
        6: ['RELEASER'],
        7: [], // Không ai được xóa
      }

      const allowedPerms = STEP_PERMISSIONS[currentStep] || []
      return allowedPerms.some(hasPerm)
    }

    // ==================================================================
    // 6. LEAD_APPROVAL (LÃNH ĐẠO ĐÃ DUYỆT) - TRƯỜNG HỢP ĐỘC LẬP
    // ==================================================================
    if (status === ELECTRONIC_DOCUMENT_STATUS.LEAD_APPROVAL) {
      const STEP_PERMISSIONS = {
        4: ['NUMBER_ISSUER'],
        5: ['DIGITAL_SIGNER'],
        6: ['RELEASER'],
        7: [], // Không ai được xóa
      }

      const allowedPerms = STEP_PERMISSIONS[currentStep] || []
      return allowedPerms.some(hasPerm)
    }

    // ==================================================================
    // 7. REGIS_NUMBER (ĐÃ CẤP SỐ) - TRƯỜNG HỢP ĐỘC LẬP
    // ==================================================================
    if (status === ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER) {
      const STEP_PERMISSIONS = {
        5: ['DIGITAL_SIGNER'],
        6: ['RELEASER'],
        7: [], // Không ai được xóa
      }

      const allowedPerms = STEP_PERMISSIONS[currentStep] || []
      return allowedPerms.some(hasPerm)
    }

    // ==================================================================
    // 8. READY (SẴN SÀNG PHÁT HÀNH) - TRƯỜNG HỢP ĐỘC LẬP
    // ==================================================================
    if (status === ELECTRONIC_DOCUMENT_STATUS.READY) {
      const STEP_PERMISSIONS = {
        6: ['RELEASER'],
        7: [], // Không ai được xóa
      }

      const allowedPerms = STEP_PERMISSIONS[currentStep] || []
      return allowedPerms.some(hasPerm)
    }

    // ==================================================================
    // 9. TRẠNG THÁI BÌNH THƯỜNG KHÁC → NGƯỜI Ở currentStep ĐƯỢC XÓA
    // ==================================================================
    const STEP_PERMISSIONS = {
      0: ['CREATE'],
      1: ['DEPARTMENT_APPROVER', 'DEPARTMENT_CO_SIGNER'],
      2: ['FORM_APPROVER'],
      3: ['LEADER_APPROVER'],
      4: ['NUMBER_ISSUER'],
      5: ['DIGITAL_SIGNER'],
      6: ['RELEASER'],
      7: [], // Đã hoàn thành, không ai được xóa
    }

    const allowedPerms = STEP_PERMISSIONS[currentStep] || []
    return allowedPerms.some(hasPerm)
  }, [selectedElectronicDocument, currentUser?.username, currentStep])

  const menuHandleDocument = useMemo(
    () => (
      <Menu style={{ borderRadius: 8, padding: 4 }}>
        <AccessControlAction
          aclActionType={ACL_ACTION_TYPE.file_history__PHAT_HANH_VB}>
          <Menu.Item
            key="file_history"
            icon={<FileOutlined style={{ color: blue }} />}
            onClick={showFileModal}>
            <span style={{ color: blue }}>Lịch sử File</span>
          </Menu.Item>
        </AccessControlAction>

        <AccessControlAction
          aclActionType={ACL_ACTION_TYPE.annex_attachment__PHAT_HANH_VB}>
          <Menu.Item
            key="file_history"
            icon={<FilePdfOutlined style={{ color: blue }} />}
            onClick={showFileModalEnnex}>
            <span style={{ color: blue }}>Phụ lục đi kèm</span>
          </Menu.Item>
        </AccessControlAction>

        {/* <AccessControlAction
          aclActionType={ACL_ACTION_TYPE.delete__PHAT_HANH_VB}> */}
        {canShowDelete && (
          <Menu.Item danger onClick={showConfirm} icon={<DeleteOutlined />}>
            Xóa trình ký
          </Menu.Item>
        )}
        {/* </AccessControlAction> */}
      </Menu>
    ),
    [
      canShowDelete,
      handleOpenEdit,
      showConfirm,
      showFileModal,
      showFileModalEnnex,
    ]
  )

  // Cập nhật currentStep khi nhận từ CanvasDoc
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep => setCurrentStep(currentStep))
    }
  }, [onStepChange])

  return (
    <Fragment>
      <DashboardLayout>
        <Helmet>
          <title>Chi tiết trình ký | Quản lý VB {systemName}</title>
        </Helmet>
        <PageTitle
          location={props.location}
          showTitle={true}
          title={selectedElectronicDocument.title}
          status={StatusTag(
            TYPE_STATUS.ELECTRONIC_DOCUMENT,
            selectedElectronicDocument?.status
          )}
          routerGoBack={getBackUrl}>
          {!selectedElectronicDocument?.assignees?.some(
            el =>
              el.permission === ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER &&
              el.assignee_code === currentUser.username
          ) && (
            <HeadingButtonWrapper style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <AccessControlAction aclActionType={ACL_ACTION_TYPE.edit__PHAT_HANH_VB}>
                <Button 
                  type="primary" 
                  icon={<EditOutlined />} 
                  onClick={() => handleOpenEdit()}
                  style={{ borderRadius: 8, display: 'flex', alignItems: 'center' }}>
                  Chỉnh sửa
                </Button>
              </AccessControlAction>
              <Dropdown overlay={menuHandleDocument} trigger={['click']}>
                <Button style={{ borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px 10px' }}>
                  <MoreOutlined style={{ color: blue }} />
                </Button>
              </Dropdown>
            </HeadingButtonWrapper>
          )}
        </PageTitle>
        <CanvasDoc
          getElectronicDocumentById={getElectronicDocumentById}
          onStepChange={step => setCurrentStep(step)} // Truyền callback để nhận currentStep
        />
      </DashboardLayout>
      <PopupAddPersonInternalDocDetail
        selectedElectronicDocument={selectedElectronicDocument}
        isVisibleAddPersonWork={isVisibleAddPersonWork}
        handleCloseAddPersonWork={handleClosePopupAddPersonWork}
      />
      <PopupUpdateSignDoc
        signDoc={selectedElectronicDocument}
        isVisiblePopupUpdateSignDoc={isVisibleEdit}
        handleClosePopupUpdateSignDoc={handleCloseEdit}
      />
      {isFileModalVisible && (
        <Modal
          title="Lịch sử File"
          visible={isFileModalVisible}
          onCancel={handleFileModalClose}
          width={1000}
          footer={[
            <Button key="back" onClick={handleFileModalClose}>
              Đóng
            </Button>,
          ]}>
          <TableFileItem historyData={historyLists} code={documentId} />
        </Modal>
      )}
      {isFileModalEnnexVisible && (
        <InternalDocumentSignDocUpdateAnnex
          visible={isFileModalEnnexVisible}
          onClose={handleFileModalEnnexClose}
          loadingAnimationStore={loadingAnimationStore}
          selectedElectronicDocument={selectedElectronicDocument}
          getElectronicDocumentById={getElectronicDocumentById}
          currentStep={currentStep} // Truyền currentStep xuống modal
        />
      )}
    </Fragment>
  )
}

export default memo(
  inject(
    'selectUserStore',
    'departmentStore',
    'loadingAnimationStore',
    'fileStore',
    'internalDocumentStore',
    'signedDocumentStore',
    'electronicDocumentStore',
    'notificationStore',
    'listHistoryLogStore',
    'authenticationStore'
  )(observer(InternalDocumentSignDocDetailPage))
)
